import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export const SkipToMain = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const href = '#main-content';
  const skipRef = useRef<HTMLAnchorElement | null>(null);
  const [isKeyboardUser, setIsKeyboardUser] = useState(false);
  const forcedFirstTabForPath = useRef<string | null>(null);
  const pointerInteractedRef = useRef(false);

  const SkipLink = styled.a`
    position: fixed;
    top: 8px;
    left: -9999px;
    z-index: 2000;

    padding: 8px 12px;
    border-radius: 8px;
    background: var(--color-white);
    color: var(--color-deep-ocean-blue);
    text-decoration: none;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);

    &:focus {
      left: 8px;
      outline: 2px solid var(--color-white);
      outline-offset: 2px;
    }
  `;

  useEffect(() => {
    pointerInteractedRef.current = false;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') setIsKeyboardUser(true);

      // ensure the first Tab lands on the skip link.
      if (e.key === 'Tab' && !e.shiftKey) {
        const active = document.activeElement;
        const hasRealFocus =
          active &&
          active !== document.body &&
          active !== document.documentElement;

        if (
          !hasRealFocus &&
          forcedFirstTabForPath.current !== location.pathname
        ) {
          forcedFirstTabForPath.current = location.pathname;
          e.preventDefault();
          skipRef.current?.focus();
        }
      }
    };
    const onPointerDown = () => {
      pointerInteractedRef.current = true;
      setIsKeyboardUser(false);
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash === '#main-content') {
      const main = document.getElementById('main-content');
      if (main) {
        main.focus();
      }
    }
  }, [location]);

  return (
    <SkipLink
      href={href}
      id="skip-to-content"
      aria-label={t('general.skipToMainContent') as string}
      ref={skipRef}
      tabIndex={isKeyboardUser ? 0 : -1}
    >
      {t('general.skipToMainContent')}
    </SkipLink>
  );
};
